import {
  ChevronDown,
  ChevronRight,
  Download,
  ExternalLink,
  FileText,
  Mail,
  MailOpen,
  Pencil,
} from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { SkeletonRows } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { StatusPill } from '../../../../components/StatusPill'
import { ADMIN_BASE_URL, adminHref } from '../../../../config/config'
import { formatCurrency, formatDate } from '../../../../lib/formatters'
import {
  EmailDirection,
  type EmailMessage,
  type LossRunDocument,
  type LossRunDocumentAttachment,
  type LossRunRequest,
  useOrgLossRunRequests,
} from '../../../../models/lossRunRequest'
import { claimCoverageLabel } from './claimForm'
import { EditClaimDialog } from './EditClaimDialog'

function formatFileSize(bytes: number) {
  if (bytes === 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`
}

function formatDateTime(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function FileRow({
  attachment,
  dateCreated,
}: {
  attachment: LossRunDocumentAttachment
  dateCreated: string
}) {
  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-2">
          <FileText className="text-muted-foreground size-4 shrink-0" />
          <span className="font-medium">{attachment.fileName}</span>
        </div>
      </TableCell>
      <TableCell className="text-muted-foreground">{formatFileSize(attachment.fileSize)}</TableCell>
      <TableCell>{formatDate(dateCreated)}</TableCell>
      <TableCell>
        <Button variant="ghost" size="icon" asChild>
          <a href={attachment.file} download={attachment.fileName} target="_blank" rel="noopener noreferrer">
            <Download className="size-4" />
          </a>
        </Button>
      </TableCell>
    </TableRow>
  )
}

function DocumentsTable({ documents }: { documents: LossRunDocument[] }) {
  const hasFiles = documents.some((d) => d.attachments.length > 0)

  if (!hasFiles) {
    return <p className="text-muted-foreground text-sm">No loss run files.</p>
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="font-semibold">File</TableHead>
          <TableHead className="font-semibold">Size</TableHead>
          <TableHead className="font-semibold">Date</TableHead>
          <TableHead className="w-12" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {documents.flatMap((doc) =>
          doc.attachments.map((att) => (
            <FileRow key={att.id} attachment={att} dateCreated={doc.dateCreated} />
          ))
        )}
      </TableBody>
    </Table>
  )
}

function LossRunHistoryTable({
  requests,
  isStaff,
  orgId,
}: {
  requests: LossRunRequest[]
  isStaff: boolean
  orgId: string
}) {
  const [editingClaimId, setEditingClaimId] = useState<string | null>(null)

  const allClaims = requests.flatMap((r) =>
    (r.lossRunClaims ?? []).map((c) => ({
      ...c,
      providerName: r.insuranceProviderContact.insuranceName,
      dateStart: r.dateStart,
      dateEnd: r.dateEnd,
    }))
  )

  if (allClaims.length === 0) {
    return <p className="text-muted-foreground text-sm">No claims data yet.</p>
  }

  const editingClaim = allClaims.find((c) => c.id === editingClaimId)

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="font-semibold">Provider</TableHead>
            <TableHead className="font-semibold">Coverage Period</TableHead>
            <TableHead className="font-semibold">Coverage Type</TableHead>
            <TableHead className="font-semibold">Incident Date</TableHead>
            <TableHead className="font-semibold">Claim Count</TableHead>
            <TableHead className="font-semibold">Paid Loss</TableHead>
            <TableHead className="font-semibold">Reserve</TableHead>
            {isStaff && <TableHead className="w-10" />}
          </TableRow>
        </TableHeader>
        <TableBody>
          {allClaims.map((claim) => (
            <TableRow key={claim.id}>
              <TableCell className="font-medium">{claim.providerName}</TableCell>
              <TableCell>
                {formatDate(claim.dateStart)} – {formatDate(claim.dateEnd)}
              </TableCell>
              <TableCell>{claimCoverageLabel(claim.coverageType)}</TableCell>
              <TableCell>{claim.incidentDate ? formatDate(claim.incidentDate) : '—'}</TableCell>
              <TableCell>{claim.claimCount ?? '—'}</TableCell>
              <TableCell>
                {claim.paidLoss != null ? formatCurrency(parseFloat(claim.paidLoss)) : '—'}
              </TableCell>
              <TableCell>{claim.reserve != null ? formatCurrency(parseFloat(claim.reserve)) : '—'}</TableCell>
              {isStaff && (
                <TableCell>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setEditingClaimId(claim.id)}
                    title="Edit claim"
                  >
                    <Pencil className="size-4" />
                  </Button>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {isStaff && editingClaim && (
        <EditClaimDialog
          claim={editingClaim}
          providerName={editingClaim.providerName}
          coveragePeriod={`${formatDate(editingClaim.dateStart)} – ${formatDate(editingClaim.dateEnd)}`}
          orgId={orgId}
          open={editingClaimId !== null}
          onOpenChange={(open) => {
            if (!open) setEditingClaimId(null)
          }}
        />
      )}
    </>
  )
}

function EmailHistory({ emails }: { emails: EmailMessage[] }) {
  if (emails.length === 0) {
    return <p className="text-muted-foreground text-sm">No emails yet.</p>
  }

  return (
    <div className="flex flex-col gap-3">
      {emails.map((email) => (
        <div key={email.id} className="border-border flex gap-3 rounded-md border p-3">
          <div className="mt-0.5">
            {email.direction === EmailDirection.OUTBOUND ? (
              <Mail className="text-muted-foreground size-4" />
            ) : (
              <MailOpen className="text-muted-foreground size-4" />
            )}
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium">
                {email.direction === EmailDirection.OUTBOUND ? 'Sent' : 'Received'}
              </span>
              <div className="flex items-center gap-2">
                <a
                  href={adminHref(`${ADMIN_BASE_URL}/insure/emailmessage/${email.id}/change/`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs"
                >
                  Email
                  <ExternalLink className="size-3" />
                </a>
                {email.agentRunId && (
                  <a
                    href={adminHref(`${ADMIN_BASE_URL}/insure/agentrun/${email.agentRunId}/change/`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs"
                  >
                    Agent Run
                    <ExternalLink className="size-3" />
                  </a>
                )}
                <span className="text-muted-foreground text-sm">{formatDateTime(email.dateCreated)}</span>
              </div>
            </div>
            <div className="text-muted-foreground text-sm">
              {email.fromEmail} → {email.toEmail}
            </div>
            <div className="text-sm font-medium">{email.subject}</div>
            {email.bodyHtml ? (
              <iframe
                srcDoc={email.bodyHtml}
                title={email.subject || 'Email'}
                className="mt-1 w-full rounded border-0"
                style={{ minHeight: 120 }}
                onLoad={(e) => {
                  const frame = e.currentTarget
                  if (frame.contentDocument) {
                    frame.style.height = `${frame.contentDocument.documentElement.scrollHeight}px`
                  }
                }}
                sandbox="allow-same-origin"
              />
            ) : (
              <div className="text-muted-foreground mt-1 line-clamp-3 text-sm whitespace-pre-line">
                {email.bodyText}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

function LossRunRequestRow({ request }: { request: LossRunRequest }) {
  const [expanded, setExpanded] = useState(false)
  const doc = request.receivedDocument

  return (
    <>
      <TableRow className="cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <TableCell>
          {expanded ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
        </TableCell>
        <TableCell className="font-medium">{request.insuranceProviderContact.insuranceName}</TableCell>
        <TableCell>{request.insuranceProviderContact.emails.join(', ') || '-'}</TableCell>
        <TableCell>
          <StatusPill status={request.status} />
        </TableCell>
        <TableCell>{formatDate(request.dateStart)}</TableCell>
        <TableCell>{formatDate(request.dateEnd)}</TableCell>
        <TableCell className="text-center">{request.emailMessages.length}</TableCell>
        <TableCell>{formatDate(request.dateCreated)}</TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={8} className="bg-muted/30 p-4">
            {doc && doc.attachments.length > 0 && (
              <div className="mb-4">
                <h4 className="mb-2 text-sm font-semibold">Received Loss Run File</h4>
                <DocumentsTable documents={[doc]} />
              </div>
            )}
            <h4 className="mb-2 text-sm font-semibold">Email History</h4>
            <EmailHistory emails={request.emailMessages} />
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

export function LossRunsTab({ orgId, isStaff = false }: { orgId: string; isStaff?: boolean }) {
  const { lossRunRequests, documents, isLoading } = useOrgLossRunRequests(orgId)

  return (
    <div className="flex flex-col gap-5">
      <Card>
        <CardHeader>
          <CardTitle>Loss Run Documents</CardTitle>
          <CardDescription>
            Loss run documents uploaded by the customer or received from providers.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? <SkeletonRows rows={3} /> : <DocumentsTable documents={documents} />}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Loss Run History</CardTitle>
          <CardDescription>Parsed claims data extracted from received loss run documents.</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <SkeletonRows rows={3} />
          ) : (
            <LossRunHistoryTable requests={lossRunRequests} isStaff={isStaff} orgId={orgId} />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Loss Run Requests</CardTitle>
          <CardDescription>
            Loss run requests sent to insurance providers. Click a row to see email history.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {lossRunRequests.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead className="font-semibold">Provider</TableHead>
                  <TableHead className="font-semibold">Contact Email</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold">Coverage Start</TableHead>
                  <TableHead className="font-semibold">Coverage End</TableHead>
                  <TableHead className="text-center font-semibold">Emails</TableHead>
                  <TableHead className="font-semibold">Requested</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lossRunRequests.map((request) => (
                  <LossRunRequestRow key={request.id} request={request} />
                ))}
              </TableBody>
            </Table>
          ) : isLoading ? (
            <SkeletonRows rows={3} />
          ) : (
            <p className="text-muted-foreground text-sm">No loss run requests found.</p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
